/**
 * L'erreur que les deux clients HTTP lèvent, **construite une seule fois** (#2228).
 *
 * Elle était écrite deux fois, à l'identique — et les deux avaient déjà divergé : le client
 * authentifié reportait le `code` machine du serveur (BE #365), le client public le jetait. Ce
 * n'était visible nulle part tant qu'aucun refus n'avait besoin d'être distingué d'un autre.
 *
 * `FEATURE_NOT_ENABLED` en a besoin, et il arrive **par les deux portes** : le guard de route pour
 * les surfaces authentifiées, deux services pour les surfaces publiques — le portail apporteur et
 * le formulaire de contact d'un tenant (suivora-api#1718).
 */

/** Ce qu'une réponse en erreur laisse au client. */
export type HttpError = Error & {
  status?: number;
  /** Le code machine du serveur, quand il en sert un. */
  code?: string;
  /**
   * La rubrique nommée par un refus `FEATURE_NOT_ENABLED`.
   *
   * Le seul champ de corps que l'on reporte au-delà du `code` : il dit **laquelle** demander à
   * l'éditeur, et sans lui le refus ne peut être rendu que par une phrase générique. Le reste du
   * corps ne remonte pas — un objet d'erreur qui porte la réponse entière invite au filtrage par
   * chaîne de caractères, exactement ce que le `code` existe pour éviter.
   */
  feature?: string;
};

/**
 * Construit l'erreur d'une réponse non-OK.
 *
 * `message` prend le texte le plus précis que le serveur donne : Nest sert `message`, certains
 * enveloppeurs `errorMessage`, et le repli reste une phrase lisible plutôt qu'un corps brut.
 */
export function buildHttpError(status: number, text: string): HttpError {
  let parsed: Record<string, unknown> = {};
  try {
    parsed = JSON.parse(text) as Record<string, unknown>;
  } catch {
    parsed = { message: text };
  }

  const raw =
    parsed.errorMessage ??
    parsed.message ??
    (typeof parsed.error === 'string' ? parsed.error : null) ??
    'Une erreur est survenue';
  const detail = Array.isArray(raw) ? raw.filter(Boolean).join(', ') : String(raw);

  const error = new Error(detail) as HttpError;
  error.status = status;
  if (typeof parsed.code === 'string') error.code = parsed.code;
  if (typeof parsed.feature === 'string') error.feature = parsed.feature;
  return error;
}
